import * as React from 'react'
import { Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Root from '@components/root/Root'
import Header from '@components/header/Header'
import ConformityCard from '@components/card/RewardConformityCard'
import SectionTitle from '@components/sectionTitle/SectionTitle'
import SortSelector from '@components/modal/SortSelector'
import ControlePrestaCard from '@components/card/ControlePrestaCard'
import Icon from '@components/icon/Icons'
import Radio from '@components/radio/Radio'
import Modal from '@components/modal/Modal'
import Tag from '@components/tag/Tag'

import useReducer from '@store/useReducer'
import * as SocieteStore from '@store/societe/societe'
import * as MetierStore from '@store/metier/metier'
import * as UserStore from '@store/user/user'
import * as SiteStore from '@store/site/site'

import api from '@api/api'
import { paramsToSearch } from '@api/utils'

import useI18n from '@i18n/useI18n'
import useBreakpoint from '@utils/useBreakpoints'
import useNavigation from '@layout/useNavigation'

import { format } from 'date-fns'
import { MODES } from './detail/utils'

import Logger from '@utils/logger'

import { ControleQualiteNature } from '@api/enum'

interface Props {
  societeId: string
}

const ControleHistoryDetailScreen = ({ societeId }: Props) => {
  const i18n = useI18n()
  const navigation = useNavigation()
  const [, bp] = useBreakpoint()

  const user = useReducer(UserStore.store, (s) => s.user)
  const site = useReducer(SiteStore.store, (s) => s.site)

  const metiers = useReducer(MetierStore.store, (s) => s.metiers)
  const [societeMetiers, setSocieteMetiers] = React.useState<ControleQualiteMetierResponse[]>([])

  const services = useReducer(SocieteStore.store, (s) => s.services)
  const societe = React.useMemo(() => services.find((s) => s.id === societeId), [services, societeId])

  const [stats, setStats] = React.useState<ControleQualiteStatistiqueResponse>()
  const [prestationStats, setPrestationStats] = React.useState<ControleQualiteStatistiqueResponse>()
  const [maintenanceStats, setMaintenanceStats] = React.useState<ControleQualiteStatistiqueResponse>()

  // other societes
  const [otherStats, setOtherStats] = React.useState<ControleQualiteStatistiqueResponse>()
  const [prestationOtherStats, setPrestationOtherStats] = React.useState<ControleQualiteStatistiqueResponse>()
  const [maintenanceOtherStats, setMaintenanceOtherStats] = React.useState<ControleQualiteStatistiqueResponse>()

  const [mode, setMode] = React.useState<'all' | 'year' | 'halfYear' | 'month'>('year')
  const [metier, setMetier] = React.useState<string>()

  const params = MODES[mode]
  const mySociete = user && user.societeId === societeId

  React.useEffect(() => {
    if (site) {
      const filter = {
        dateDebut: format(params.dateDebut, 'yyyy-MM-dd'),
        dateFin: format(params.dateFin, 'yyyy-MM-dd'),
        societeIds: [societeId],
        siteIds: [site.id],
      }

      // get all controle for this societe and extract audited metiers
      api.controle
        .all(filter)
        .then((controles) => {
          const audited = metiers.filter((m) => controles.some((c) => c.metierId === m.id))
          setSocieteMetiers(audited)

          if (!audited.some((m) => m.id === metier)) {
            setMetier(audited[0]?.id)
          }
        })
        .catch((err) => {
          setSocieteMetiers([])
          Logger.error(err)
        })
    }
  }, [params, metiers.length, !!site])

  React.useEffect(() => {
    if (site) {
      const filter = {
        dateDebut: format(params.dateDebut, 'yyyy-MM-dd'),
        dateFin: format(params.dateFin, 'yyyy-MM-dd'),
        societeId,
        metierId: metier,
        siteId: site.id,
      }

      Promise.all([
        api.controle.stats(filter),
        api.controle.stats({ ...filter, nature: ControleQualiteNature.PRESTATION }),
        api.controle.stats({ ...filter, nature: ControleQualiteNature.MAINTENANCE }),
      ])
        .then(([st, pr, ma]) => {
          setStats(st)
          setPrestationStats(pr)
          setMaintenanceStats(ma)
        })
        .catch((err) => {
          setStats(undefined)
          setPrestationStats(undefined)
          setMaintenanceStats(undefined)
          Logger.error(err)
        })

      if (!mySociete) {
        Promise.all([
          api.controle.stats({ ...filter, exclureSociete: true }),
          api.controle.stats({ ...filter, nature: ControleQualiteNature.PRESTATION, exclureSociete: true }),
          api.controle.stats({ ...filter, nature: ControleQualiteNature.MAINTENANCE, exclureSociete: true }),
        ])
          .then(([st, pr, ma]) => {
            setOtherStats(st)
            setPrestationOtherStats(pr)
            setMaintenanceOtherStats(ma)
          })
          .catch((err) => {
            setOtherStats(undefined)
            setPrestationOtherStats(undefined)
            setMaintenanceOtherStats(undefined)
            Logger.error(err)
          })
      }
    }
  }, [params, metier, mySociete, site])

  if (!societe) {
    return (
      <Root header={i18n.t('screens.controles.title')} fixed onLeft={navigation.goBack}>
        <Error>{i18n.t('screens.controles.form.presta.empty')}</Error>
      </Root>
    )
  }

  const headerLinks = [
    { label: i18n.t('screens.controles.title'), onClick: () => navigation.jumpTo('/controles') },
    {
      label: societe.nameSociete,
      onClick: () => navigation.jumpTo(`/controles/details/infos?societeId=${societeId}`),
    },
    { label: i18n.t('screens.controleDetail.detail'), onClick: () => null },
  ]

  const options = Object.keys(MODES).map((key) => ({ label: i18n.t(`screens.controleDetail.sort.${key}`), value: key }))
  const option = options.find((opt) => opt.value === mode)

  const metierOptions = societeMetiers.map((m) => ({ label: m.libelle, value: m.id }))
  const metierOption = metierOptions.find((opt) => opt.value === metier)

  const renderMetierFilter = () => {
    return (
      <MetierContainer>
        <Radio
          iconOnTheRight
          horizontalPadding
          data={metierOptions}
          value={metierOption}
          onItemSelected={(val) => {
            setMetier(val.value)
            Modal.close()
          }}
        />
      </MetierContainer>
    )
  }

  const openFilter = () => {
    Modal.open({ Content: renderMetierFilter, position: 'bottom' })
  }

  const renderInfos = () => {
    if (!stats || !societe) {
      return null
    }

    if (bp === 'phone') {
      return (
        <InfosContainer>
          <DualLine>
            <Title>{i18n.t('screens.controleDetail.result')}</Title>

            <SortFilter>
              <SortSelector
                sorts={options}
                sort={option}
                reverse
                onSortChange={(opt) => {
                  if (opt) {
                    setMode(opt.value)
                  }
                }}
                title=""
                color={`${theme.colors.background}99`}
              />

              <Icon name="arrow-down" size={20} color={`${theme.colors.background}99`} />
            </SortFilter>
          </DualLine>

          <ControlePrestaCard
            societeId={societeId}
            stats={stats}
            statsOthers={otherStats}
            title={i18n.t('screens.controleDetail.type.all')}
            onNavigation={(path, params) =>
              navigation.push(`${path}${paramsToSearch({ ...params, metierId: metier })}`)
            }
          />
        </InfosContainer>
      )
    }

    return (
      <>
        <InfosContainer>
          <TotalContainer>
            <TotalCount>{stats.nombreControle}</TotalCount>
            <Total>{i18n.t('screens.controleDetail.count', { count: stats.nombreControle })}</Total>
          </TotalContainer>

          <Infos>
            <InfoCard
              activeOpacity={0.8}
              onPress={() =>
                navigation.push(
                  `/controles/history/list${paramsToSearch({ societeId, metierId: metier, observation: false })}`
                )
              }>
              <InfoCount>{stats.nombreControleSansObservation}</InfoCount>
              <Info>{i18n.t('screens.controleDetail.noObservation')}</Info>
            </InfoCard>
            <InfoCard
              activeOpacity={0.8}
              onPress={() =>
                navigation.push(
                  `/controles/history/list${paramsToSearch({ societeId, metierId: metier, observation: true })}`
                )
              }>
              <InfoCount>{stats.nombreControleAvecObservation}</InfoCount>
              <Info>{i18n.t('screens.controleDetail.observation')}</Info>
            </InfoCard>
            <InfoCard
              activeOpacity={0.8}
              onPress={() =>
                navigation.push(
                  `/controles/history/list${paramsToSearch({ societeId, metierId: metier, conforme: false })}`
                )
              }>
              <InfoCount>{stats.nombreControleNonConforme}</InfoCount>
              <Info>{i18n.t('screens.controleDetail.noConforme')}</Info>
            </InfoCard>
          </Infos>

          <ConformityCard score={stats.tauxConformiteMoyen} />
        </InfosContainer>

        <Sort>
          <Tag
            item={{
              label: metierOption
                ? i18n.t('screens.controleDetail.metiers', { metier: metierOption.label })
                : i18n.t('screens.controleDetail.noMetiers'),
              value: '',
            }}
            onItemSelected={openFilter}
            selected={!!metierOption}
            inactiveColor
            disabled={metierOptions.length <= 1}
          />

          <SortSelector
            sorts={options}
            sort={option}
            reverse
            onSortChange={(opt) => {
              if (opt) {
                setMode(opt.value)
              }
            }}
          />
        </Sort>
      </>
    )
  }

  return (
    <Root
      header={societe.nameSociete}
      onLeft={navigation.goBack}
      renderSubHeader={renderInfos}
      right={metierOptions.length > 1 ? 'filter' : undefined}
      onRight={openFilter}>
      {bp !== 'phone' && <Header links={headerLinks} title={societe.nameSociete} renderBottom={renderInfos} />}

      <ContentContainer>
        <SectionTitle title={i18n.t('screens.controleDetail.detail')} />

        <Items>
          {bp !== 'phone' && (
            <Container>
              <ControlePrestaCard
                societeId={societeId}
                stats={stats}
                statsOthers={otherStats}
                title={i18n.t('screens.controleDetail.type.all')}
                onNavigation={(path, params) =>
                  navigation.push(`${path}${paramsToSearch({ ...params, metierId: metier })}`)
                }
              />
            </Container>
          )}

          <Container>
            <ControlePrestaCard
              societeId={societeId}
              stats={prestationStats}
              statsOthers={prestationOtherStats}
              title={i18n.t('screens.controleDetail.type.prestation')}
              onNavigation={(path, params) =>
                navigation.push(`${path}${paramsToSearch({ ...params, metierId: metier, nature: 'PRESTATION' })}`)
              }
            />
          </Container>

          <Container>
            <ControlePrestaCard
              societeId={societeId}
              stats={maintenanceStats}
              statsOthers={maintenanceOtherStats}
              title={i18n.t('screens.controleDetail.type.maintenance')}
              onNavigation={(path, params) =>
                navigation.push(`${path}${paramsToSearch({ ...params, metierId: metier, nature: 'MAINTENANCE' })}`)
              }
            />
          </Container>
        </Items>
      </ContentContainer>
    </Root>
  )
}

export default ControleHistoryDetailScreen

const InfosContainer = styled(View)`
  padding: 32px 0;
  ${(props) => props.theme.breakpoint !== 'phone' && 'flex-direction: row; flex-wrap: wrap'};
  ${(props) => props.theme.breakpoint === 'phone' && 'padding: 0px; padding-top: 24px'};
`

const TotalCount = styled(Text)`
  ${(props) => props.theme.fonts.Heading1};
  color: ${(props) => props.theme.colors.secondaryText};
`

const Total = styled(Text)`
  ${(props) => props.theme.fonts.Title2};
  color: ${(props) => props.theme.colors.secondaryText};
`

const TotalContainer = styled(View)`
  margin-right: 24px;
`

const Infos = styled(View)`
  flex-direction: row;
  flex-grow: 1;
  margin: 8px 0;
`

const InfoCard = styled(TouchableOpacity)`
  background-color: ${(props) => props.theme.colors.background};
  border-radius: 4px;
  border: 1px solid ${(props) => props.theme.colors.cardSecondary};
  padding: 16px;
  align-items: center;
  flex: 1;
  margin-right: 16px;
  max-width: 250px;
  min-width: 100px;
`

const InfoCount = styled(Text)`
  ${(props) => props.theme.fonts.Title3};
  font-size: 24px;
  line-height: 28px;
  margin-bottom: 8px;
`

const Info = styled(Text)`
  ${(props) => props.theme.fonts.Subheadline};
  color: ${(props) => props.theme.colors.secondaryText};
  text-align: center;
`

const ContentContainer = styled(View)`
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? 16 : 32)}px;
`

const Container = styled(View)`
  ${(props) => (props.theme.breakpoint === 'phone' ? 'margin: 8px 0px' : 'margin: 0px 16px 16px 0px')};
`

const DualLine = styled(View)`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`

const SortFilter = styled(View)`
  flex-direction: row;
  align-items: center;
`

const Title = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
  color: ${(props) => props.theme.colors.background};
`

const Items = styled(View)`
  flex-direction: ${(props) => (props.theme.breakpoint === 'phone' ? 'column' : 'row')};
  margin-top: ${(props) => (props) => props.theme.breakpoint === 'phone' ? 8 : 16}px;
  ${(props) => props.theme.breakpoint !== 'phone' && 'flex-wrap: wrap'};
`

const Sort = styled(View)`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-right: 32px;
`

const Error = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  margin: 24px;
  text-align: center;
`

const MetierContainer = styled(View)`
  background-color: ${(props) => props.theme.colors.background};
  padding: 16px 24px;
  border-radius: 8px;
  min-width: 300px;
  margin-bottom: 40px;
`
